import { useMemo } from 'react';

import {
  Configuration,
  NextPortApi,
  type NextPortArrival,
  type NextportArrivalsClusterGet200Response,
} from '@ais-ui/api-spec/shiptracker';
import { useSession } from '@ais-ui/websockets';
import { UseQueryResult } from '@tanstack/react-query';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials, parseDate } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

export interface NextPortArrivalWithDate {
  imo: number;
  eta: Date;
  locode: string;
  id: number;
  probability: number;
}

interface NextportArrivalsClusterGetDatesConverted200Response {
  /**
   *
   * @type {Array<NextPortArrival>}
   * @memberof NextportArrivalsClusterGet200Response
   */
  records: Array<NextPortArrivalWithDate>;
}

const useNextportApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  const username = credentials?.username || '';
  const password = credentials?.password || '';

  const authHeader = getAuthHeaderFromCredentials(username, password);

  if (hostname.includes('localhost')) {
    basePath = 'https://maritime.gatehouse.dk/api/shiptracker';
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
    // This is a special case, because nextport ONLY works on prod
  } else if (!hostname.includes('mdm.gatehousemaritime')) {
    basePath = 'https://maritime.gatehouse.dk/api/shiptracker';
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}/aisweb/api/shiptracker`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const nextportApi = useMemo(
    () => new NextPortApi(configuration),
    [configuration]
  );

  // The wrapper is used to get error messages from the backend, as they are otherwise thrown out
  return apiClientWrapper(nextportApi) as NextPortApi;
};

export const useNextportShipImoGet = (
  imo: string
): UseQueryResult<NextportArrivalsClusterGetDatesConverted200Response> => {
  const nextportApi = useNextportApi();
  return useQuery(['nextportImo', imo], async () => {
    const response = await nextportApi.nextportShipImoGet({ imo: imo });

    const parsedRecords = response.records.map((record) => ({
      ...record,
      eta: parseDate(record.eta),
    }));

    return { ...response, records: parsedRecords };
  });
};

export const useNextportPortGet = (
  locode: string
): UseQueryResult<NextportArrivalsClusterGetDatesConverted200Response> => {
  const nextportApi = useNextportApi();

  return useQuery(
    ['nextportLocode', locode],
    async () => {
      // Fetch data from the API
      const response = await nextportApi.nextportArrivalsClusterGet({
        cluster: locode,
      });

      const parsedRecords = response.records.map((record) => ({
        ...record,
        eta: parseDate(record.eta),
      }));

      return { ...response, records: parsedRecords };
    },
    {
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );
};
